import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MainImage from 'pages/wholeme/molecules/mainImage/MainImage';
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ProfileIntakeStepPage from '../../../molecules/profileIntakeStepPage/ProfileIntakeStepPage';
import BottomDrawer from '../../../../../common/molecules/BottomDrawer/BottomDrawer';
import { useAppState } from '../../../../../providers/app-state/app-state-provider';
import isValidUrl from '../../../../../helpers/isValidUrl';
import appStrings from '../../../../../common/app_strings';
import { InfoIconGrey } from '../../../icons/Icons';
import { BlueBriefcaseSvg } from '../../../../../common/atoms/adornments/InputIcons';
import { lightThemeStyles, inputFieldStyles } from '../../../../../common/atoms/inputField/inputFieldStyles';
import { ctaButtonStyle } from '../../../../../common/molecules/BottomDrawer/bottomDrawerStyles';
import appColors from '../../../../../common/app_colors';

const mainImageData = {
  imageSrc: './assets/icons/intake-pages/linkedin-logo-blue-triangles.svg',
  imageAlt: 'linkedin logo with blue triangles',
};

const iconOverrideStyles = {
  position: 'relative',
  fontSize: '24px',
};

const imageStylesOverride = {
  margin: '0',
  width: '100%',
  paddingLeft: '20px',
  marginBottom: '-1.5rem',
};

const linkedInRegex = /^https?:\/\/(?:www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+(?:-[a-zA-Z0-9]+)?\/?(?:\?.*)?$/i;

const CssTextField = styled(TextField)({
  ...lightThemeStyles,
  ...inputFieldStyles,
});

const textStyle = {
  fontSize: '13px',
  color: appColors.buttons.secondaryBackground,
  fontWeight: 700,
  textAlign: 'center',
  cursor: 'pointer',
};

const ProfileLinkedInPage = ({
  paginationMeta = { current: 0, total: 0 },
  nextOnClick,
  prevOnClick,
  wholeMeClient,
  exitIconOnClickOverride,
}) => {
  const [linkedinurl, setlinkedinurl] = useState(null);
  const [professionalUrl, setProfessionalUrl] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [drawerText, setDrawerText] = useState('');

  const { wholeMe: { wholeMeLinkedinIntake } } = appStrings;
  const { platform: { isNativePlatform } } = useAppState();

  const saveLinkedInState = (val) => {
    wholeMeClient.saveLinkedInState([
      {
        category_id: 'linkedIn',
        question_id: 'linkedIn_url',
        value: val,
      },
    ]).then(() => {
      nextOnClick();
    });
  };

  const saveProfessionalState = (val) => {
    wholeMeClient.saveProfessionalState([
      {
        category_id: 'professional',
        question_id: 'professional_url',
        value: val,
      },
    ]).then(() => {
      nextOnClick();
    });
  };

  const saveLinkedIn = () => {
    setErrorMessage('');
    if (linkedinurl === null || linkedinurl.trim() === '') {
      saveLinkedInState(null);
    } else if (!isValidUrl(linkedinurl, linkedInRegex).isValid) {
      setErrorMessage({
        linkedin: wholeMeLinkedinIntake.errMsg,
      });
    } else {
      saveLinkedInState(isValidUrl(linkedinurl, linkedInRegex).url);
    }
  };

  const saveProfessional = () => {
    setErrorMessage('');
    if (professionalUrl === null || professionalUrl.trim() === '') {
      saveProfessionalState(null);
    } else if (!isValidUrl(professionalUrl).isValid) {
      setErrorMessage({
        professional: wholeMeLinkedinIntake.errMsg,
      });
    } else {
      saveProfessionalState(isValidUrl(professionalUrl).url);
    }
  };

  const handleInfoIconClick = (type) => {
    switch (type) {
      case 'professional':
        setDrawerText({
          title: wholeMeLinkedinIntake.professionalTitle,
          subtitle: wholeMeLinkedinIntake.professionalSubtitle,
        });
        break;
      case 'howTo':
        setDrawerText({
          title: wholeMeLinkedinIntake.howToTitle,
          subtitle: isNativePlatform ? wholeMeLinkedinIntake.nativeHowToSubtitle : wholeMeLinkedinIntake.webHowToSubtitle,
        });
        break;
      default:
        break;
    }
    setIsDrawerOpen(!isDrawerOpen);
  };

  const renderDrawerTitle = () => {
    if (drawerText?.title === wholeMeLinkedinIntake.howToTitle) {
      return (
        <Typography
          style={{
            fontSize: '18px',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {drawerText?.title}
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          style={{
            fontSize: '18px',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {drawerText?.title}
        </Typography>
        <Button
          onClick={saveProfessional}
          type="button"
          style={ctaButtonStyle}
        >
          <span>{appStrings.wholeMe.save}</span>
        </Button>
      </Box>
    );
  };

  const renderDrawerText = () => {
    const drawerData = drawerText?.subtitle;
    if (drawerText?.title === wholeMeLinkedinIntake.howToTitle) {
      return (
        <ol
          style={{
            fontSize: '16px',
            fontWeight: 600,
          }}
        >
          {drawerData.map((item, index) => (
            <li key={item}>
              {(isNativePlatform && index === drawerData.length - 1) ? (
                <>
                  {wholeMeLinkedinIntake.locateUrl}
                  {' '}
                  <span style={{ color: '#5981D1' }}>{item}</span>
                </>
              ) : (
                item
              )}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
      >
        <Typography sx={{ fontWeight: 500 }}>{drawerData}</Typography>
        <CssTextField
          fullWidth
          placeholder={wholeMeLinkedinIntake.urlPlaceholder}
          value={professionalUrl}
          size="small"
          onChange={(e) => setProfessionalUrl(e.target.value)}
          error={!!errorMessage.professional && { message: errorMessage.professional }}
          helperText={errorMessage?.professional}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BlueBriefcaseSvg styles={iconOverrideStyles} />
              </InputAdornment>
            ),
            style: {
              backgroundColor: appColors.primaryAppBackground,
            },
          }}
          sx={{
            '& fieldset': { border: 'none' },
          }}
        />
        <Typography
          style={textStyle}
          onClick={nextOnClick}
        >
          {wholeMeLinkedinIntake.dontHaveEither}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    void (async () => {
    const state = (await wholeMeClient.fetchLinkedInState())?.linkedIn_url || [];
    setlinkedinurl(state[0]?.value || '');
  })();
  }, []);
  return (
    <ProfileIntakeStepPage
      paginationMeta={paginationMeta}
      nextButton={{ onClick: saveLinkedIn }}
      prevButton={{ onClick: prevOnClick }}
      title={wholeMeLinkedinIntake.title}
      subtitle={wholeMeLinkedinIntake.subtitle}
      backgroundOverlay="assets/overlays/bottom-circle-mini-top-circle-overlay.svg"
      exitIconOnClickOverride={exitIconOnClickOverride}
      skipPersonalization
    >
      <Box>
        <CssTextField
          fullWidth
          placeholder={wholeMeLinkedinIntake.inputPlaceholder}
          value={linkedinurl}
          size="small"
          onChange={(e) => setlinkedinurl(e.target.value)}
          error={!!errorMessage.linkedin && { message: errorMessage.linkedin }}
          helperText={errorMessage?.linkedin}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BlueBriefcaseSvg styles={iconOverrideStyles} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="info-icon"
                  onClick={() => handleInfoIconClick('howTo')}
                  disableRipple
                  style={{ padding: 0, marginRight: 0 }}
                >
                  <InfoIconGrey />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& fieldset': { border: 'none' },
          }}
        />
        <Typography
          style={{
            ...textStyle,
            marginTop: '1rem',
          }}
          onClick={() => handleInfoIconClick('professional')}
        >
          {wholeMeLinkedinIntake.dontHave}
        </Typography>
      </Box>
      <MainImage
        imageSrc={mainImageData.imageSrc}
        imageAlt={mainImageData.imageAlt}
        imageStylesOverride={imageStylesOverride}
      />
      <BottomDrawer
        title={renderDrawerTitle()}
        subTitle={renderDrawerText()}
        showBackdrop={false}
        open={isDrawerOpen}
        toggleDrawer={() => setIsDrawerOpen(!isDrawerOpen)}
        hideCta
        containerStylesOverride={{ justifyContent: 'center' }}
      />
    </ProfileIntakeStepPage>
  );
};

export const ProfileLinkedInPagePropTypes = {
  nextButton: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
  prevButton: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
  paginationMeta: PropTypes.shape({ current: PropTypes.number, total: PropTypes.number }),
};

export const ProfileLinkedInpageDefaultProps = {
  nextButton: { onClick: () => { } },
  prevButton: { label: '', onClick: () => { } },
  paginationMeta: { current: 0, total: 0 },
};

ProfileLinkedInPage.propTypes = ProfileLinkedInPagePropTypes;

export default ProfileLinkedInPage;
